'use client';

import { useEffect, useRef, useState } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { ExpandIcon, Cancel01Icon } from '@terra/ui-icons';
import type { SseSubscribe } from '@terra/shared-sse-contracts';
import {
  VOYAGER_SCREENCAST_FRAME_SSE_EVENT,
  type VoyagerScreencastFramePayload,
} from '@terra/shared-dto-chat-types';
import { Text } from '../../../components/styled/text';
import { Button } from '../../../components/button';
import { LegacyDialog } from '../../../components/dialog';
import { IconButton } from '../../../components/icon-button';

export type VoyagerScreencastFrameData = VoyagerScreencastFramePayload;
export type BrowserStreamTabData = {
  streamingId: string;
};
export type OnOpenBrowserStreamInMainContent = (
  data: BrowserStreamTabData,
) => void;

export function resolveScreencastFrameAcceptance({
  frameNumber,
  latestFrameNumber,
}: {
  frameNumber: number;
  latestFrameNumber: number;
}): { accept: boolean; nextLatestFrameNumber: number } {
  if (frameNumber === latestFrameNumber) {
    return { accept: false, nextLatestFrameNumber: latestFrameNumber };
  }

  return { accept: true, nextLatestFrameNumber: frameNumber };
}

type AiBrowserStreamingFrameProps = {
  streamingId?: string;
  onSSESubscribe?: SseSubscribe;
  /** Control modal open state externally */
  isModalOpen?: boolean;
  /** Callback when modal open state changes */
  onModalOpenChange?: (open: boolean) => void;
  /** If true, only show modal (no inline preview) */
  modalOnly?: boolean;
  /** If true, render full-size standalone view with no dialog (for main content tabs) */
  standalone?: boolean;
  /** Callback fired when the first frame is received (streaming started) */
  onStarted?: () => void;
  /** When provided, expand button opens a tab instead of a dialog */
  onOpenInMainContent?: OnOpenBrowserStreamInMainContent;
};

export function AiBrowserStreamingFrame({
  streamingId,
  onSSESubscribe,
  isModalOpen: externalIsModalOpen,
  onModalOpenChange,
  modalOnly = false,
  standalone = false,
  onStarted,
  onOpenInMainContent,
}: AiBrowserStreamingFrameProps) {
  const [internalIsModalOpen, setInternalIsModalOpen] = useState(false);
  const [voyagerFrame, setVoyagerFrame] = useState<{
    frame: string;
    frameNumber: number;
    format: 'jpeg' | 'png';
  } | null>(null);
  const onStartedRef = useRef(onStarted);
  const latestFrameNumberRef = useRef<number>(-1);
  const hasStartedNotifiedRef = useRef(false);

  // Keep ref updated with latest callback
  useEffect(() => {
    onStartedRef.current = onStarted;
  }, [onStarted]);
  useEffect(() => {
    latestFrameNumberRef.current = -1;
    hasStartedNotifiedRef.current = false;
    setVoyagerFrame(null);
  }, [streamingId]);
  // Use external control if provided, otherwise use internal state
  const isStreamingDialogOpen =
    externalIsModalOpen !== undefined
      ? externalIsModalOpen
      : internalIsModalOpen;
  const setIsStreamingDialogOpen = (open: boolean) => {
    if (onModalOpenChange) {
      onModalOpenChange(open);
    } else {
      setInternalIsModalOpen(open);
    }
  };

  // Subscribe to SSE updates for voyager screencast frames
  // Only subscribe when streamingId is available to avoid processing frames from wrong streams
  useEffect(() => {
    if (!onSSESubscribe || !streamingId) {
      return;
    }

    const unsubscribe = onSSESubscribe(
      VOYAGER_SCREENCAST_FRAME_SSE_EVENT,
      (frame) => {
        if (frame.streamingId !== streamingId) {
          return;
        }

        const { accept, nextLatestFrameNumber } =
          resolveScreencastFrameAcceptance({
            frameNumber: frame.frameNumber,
            latestFrameNumber: latestFrameNumberRef.current,
          });

        if (!accept) {
          return;
        }

        latestFrameNumberRef.current = nextLatestFrameNumber;
        if (!hasStartedNotifiedRef.current) {
          hasStartedNotifiedRef.current = true;
          onStartedRef.current?.();
        }

        setVoyagerFrame({
          frame: frame.frame,
          frameNumber: frame.frameNumber,
          format: frame.format,
        });
      },
    );

    return unsubscribe;
  }, [onSSESubscribe, streamingId]);
  const frameLabel = voyagerFrame
    ? `Live Browser View - Frame #${voyagerFrame.frameNumber}`
    : 'Live Browser View - Waiting for frames...';
  const frameImage = voyagerFrame ? (
    <img
      src={`data:image/${voyagerFrame.format};base64,${voyagerFrame.frame}`}
      alt={`Browser frame ${voyagerFrame.frameNumber}`}
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        display: 'block',
      }}
    />
  ) : (
    <Text color="fg.muted" fontSize="sm">
      Waiting for browser frames...
    </Text>
  );

  // Full-size standalone view (used in main content tabs)
  if (standalone) {
    return (
      <VStack alignItems="start" gap={1} width="100%" height="100%">
        <HStack
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          flexShrink={0}
        >
          <HStack gap={2} alignItems="center">
            <Box
              position="relative"
              width="8px"
              height="8px"
              borderRadius="full"
              bg="red.500"
              animationStyle="pulse"
            />
            <Text fontSize="xs" fontWeight="medium" color="fg.default">
              {frameLabel}
            </Text>
          </HStack>
          {onOpenInMainContent && streamingId && (
            <Button
              variant="ghost"
              size="xs"
              colorScheme="gray"
              p={1}
              minWidth="auto"
              height="auto"
              borderRadius="sm"
              onClick={() => onOpenInMainContent({ streamingId })}
            >
              <ExpandIcon size={12} />
            </Button>
          )}
        </HStack>
        <Box
          width="100%"
          flex="1"
          minH="200px"
          borderRadius="sm"
          overflow="hidden"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="bg.canvas"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {frameImage}
        </Box>
      </VStack>
    );
  }

  // Inline preview mode: return null until first frame arrives (unless modalOnly)
  if (!voyagerFrame && !modalOnly) {
    return null;
  }

  return (
    <>
      {!modalOnly && voyagerFrame && (
        <VStack alignItems="start" gap={1} width="100%">
          <HStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <HStack gap={2} alignItems="center">
              <Box
                position="relative"
                width="8px"
                height="8px"
                borderRadius="full"
                bg="red.500"
                animationStyle="pulse"
              />
              <Text fontSize="xs" fontWeight="medium" color="fg.default">
                {frameLabel}
              </Text>
            </HStack>
            <HStack gap={1}>
              <Button
                variant="ghost"
                size="xs"
                colorScheme="gray"
                p={1}
                minWidth="auto"
                height="auto"
                borderRadius="sm"
                onClick={() => {
                  if (onOpenInMainContent && streamingId) {
                    onOpenInMainContent({ streamingId });
                  } else {
                    setIsStreamingDialogOpen(true);
                  }
                }}
              >
                <ExpandIcon size={12} />
              </Button>
            </HStack>
          </HStack>
          <Box
            width="100%"
            height="200px"
            borderRadius="sm"
            overflow="hidden"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="bg.canvas"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <img
              src={`data:image/${voyagerFrame.format};base64,${voyagerFrame.frame}`}
              alt={`Browser frame ${voyagerFrame.frameNumber}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
        </VStack>
      )}

      <LegacyDialog.Root
        open={isStreamingDialogOpen}
        onOpenChange={({ open }) => !open && setIsStreamingDialogOpen(false)}
        lazyMount
        unmountOnExit
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content
            width="100vw"
            height="100vh"
            position="fixed"
            inset={0}
            zIndex={9999}
          >
            <VStack
              gap={6}
              p={6}
              alignItems="flex-start"
              width="full"
              height="full"
            >
              <HStack
                gap={2}
                alignItems="center"
                width="full"
                justifyContent="space-between"
              >
                <HStack gap={2} alignItems="center">
                  <Box
                    position="relative"
                    width="8px"
                    height="8px"
                    borderRadius="full"
                    bg="red.500"
                    animationStyle="pulse"
                  />
                  <LegacyDialog.Title>{frameLabel}</LegacyDialog.Title>
                </HStack>
                <LegacyDialog.CloseTrigger asChild>
                  <IconButton
                    aria-label="Close dialog"
                    variant="ghost"
                    size="sm"
                  >
                    <Cancel01Icon size={16} />
                  </IconButton>
                </LegacyDialog.CloseTrigger>
              </HStack>

              <Box
                p={4}
                bg="bg.canvas"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.default"
                width="full"
                height="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
              >
                {frameImage}
              </Box>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}
